import React, { ReactElement } from "react";
import { SimpleToast } from "@coworker/apprestructured/src/shared/simple/SimpleToast/SimpleToast";
import { useTranslation } from "@coworker/locales";

const defaultVisibilityInSeconds = 3;
const undoDelayInSeconds = 3;

type ToastContextState = {
  text: string | ReactElement;
  isToastVisible: boolean;
  actionText?: string | React.ReactNode;
  actionHandler?: (() => void) | undefined;
  start: number;
  duration: number | undefined;
};

type ToastContextType = [
  ToastContextState | null,
  React.Dispatch<React.SetStateAction<ToastContextState | null>>,
];

const ToastContext = React.createContext<ToastContextType>([null, () => {}]);

function useToastNotification() {
  const [state, setState] = React.useContext(ToastContext);
  const { t } = useTranslation();
  const showToast = (
    text: string | ReactElement,
    actionText?: string | React.ReactNode,
    actionHandler?: () => void,
    duration: number = defaultVisibilityInSeconds
  ) => {
    setState({
      text: text ?? "",
      isToastVisible: true,
      actionText: actionText ?? "",
      actionHandler: actionHandler ?? (() => {}),
      start: Date.now(),
      duration,
    });
  };
  const showToastWithUndo = (
    text: string | ReactElement,
    undoHandler: () => void,
    duration?: number
  ) => {
    setTimeout(
      () =>
        setState({
          text,
          isToastVisible: true,
          actionText: t("undoString"),
          actionHandler: () => {
            undoHandler();
            setState(null);
          },
          start: Date.now() + undoDelayInSeconds * 1000,
          duration,
        }),
      250
    );
  };

  return { noToastShown: !state?.text, showToast, showToastWithUndo };
}

type ToastNotificationProviderProps = {
  children: React.ReactNode;
};

function ToastNotificationProvider({
  children,
}: ToastNotificationProviderProps) {
  const [state, setState] = React.useState<ToastContextState | null>(null);
  return (
    <ToastContext.Provider value={[state, setState]}>
      {children}
    </ToastContext.Provider>
  );
}

function ToastNotification() {
  const { t } = useTranslation();
  const [state, setState] = React.useContext(ToastContext);
  const { text, isToastVisible, actionText, actionHandler, start, duration } =
    state || {};

  React.useEffect(() => {
    if (!start) return;

    const now = Date.now();
    const durationInSeconds = duration || defaultVisibilityInSeconds;
    const end = start + durationInSeconds * 1000;

    const timeout = setTimeout(() => {
      setState(null);
    }, end - now);

    return () => clearTimeout(timeout);
  }, [start, duration, setState]);

  const onClose = () => {
    setState(null);
  };

  return (
    <div>
      {isToastVisible && (
        <SimpleToast
          text={text ?? ""}
          isOpen={isToastVisible}
          onCloseRequest={onClose}
          actionButtonText={actionText ?? ""}
          actionClick={actionHandler ?? (() => {})}
          ariaLabelCloseBtn={t("dismissNotificationString")}
          ariaLabel={""}
        />
      )}
    </div>
  );
}

export { ToastNotification, ToastNotificationProvider, useToastNotification };
